import { useMemo } from 'react'
import { useOrganization } from '@clerk/clerk-react'

import { mergeWorkflows } from '@/features/workflows/merge'
import type { WorkflowView } from '@/features/workflows/types'
import {
  useOrgWorkflowsQuery,
  useWorkflowsQuery,
} from '@/features/workflows/hooks/useWorkflowsQuery'

interface WorkflowViewsResult {
  data: WorkflowView[]
  isLoading: boolean
  /**
   * The db rows failed, so there is no trustworthy list to draw at all.
   *
   * Deliberately not the catalogue: see `isCatalogueUnavailable`.
   */
  isError: boolean
  /**
   * Nebula's catalogue is unreachable while the db rows loaded fine.
   *
   * Its own state rather than part of `isError` because it is a *supported*
   * configuration, not only an outage: `nebula_api_url` is optional in
   * `hoku/config.py`, `.env.example` ships `NEBULA_API_URL` commented out, and
   * #924's deploy workflows make an absent Nebula explicitly non-fatal. Folding
   * it into `isError` put this page permanently behind a Retry that cannot
   * succeed in local dev and in any environment that never set the variable.
   *
   * The caller must say the catalogue is degraded rather than report "no
   * workflows defined", which is the one reading that is certainly wrong.
   */
  isCatalogueUnavailable: boolean
  /**
   * Clerk has settled on no active organization, so there is nothing to fetch.
   * Distinct from `isLoading`: the caller should say so rather than wait.
   */
  hasNoOrg: boolean
  refetch: () => void
}

/**
 * The Workflows page's list: Nebula's org catalogue joined to the
 * `app_nova_workflows` rows (see `mergeWorkflows` for which source wins what).
 *
 * The two sources fail differently, so their statuses stay separate. A db outage
 * is fatal: every row's session counts and duty links come from there, and
 * showing them all as zero is worse than showing nothing. A missing catalogue is
 * not, because an environment with no Nebula is a supported state — the page
 * says the catalogue is degraded and draws what merged, which today is nothing,
 * since `mergeWorkflows` iterates the catalogue. That's still strictly better
 * than the old behaviour: the page stops claiming the org has no workflows.
 */
export function useWorkflowViews(): WorkflowViewsResult {
  const { organization, isLoaded: isOrgLoaded } = useOrganization()
  const catalogue = useOrgWorkflowsQuery()
  const records = useWorkflowsQuery()

  const data = useMemo(
    () => mergeWorkflows(catalogue.data ?? [], records.data ?? []),
    [catalogue.data, records.data]
  )

  const isError = records.isError
  // Reported only when the db rows are fine. A failure of both is one story —
  // the page is down — and the degraded-catalogue notice would just be noise on
  // top of the error state.
  const isCatalogueUnavailable = catalogue.isError && !records.isError
  // The catalogue query is disabled without an org id, and a disabled query sits
  // in `pending` forever in TanStack Query v5 — never `error`, so nothing below
  // releases it. A session that never gets an org (a Careforce global admin with
  // none selected, or Clerk resolving a deleted one) would hold the skeleton
  // indefinitely, so treat "settled on no org" as its own state.
  const hasNoOrg = isOrgLoaded && !organization?.id

  return {
    data,
    // `catalogue` stays pending while it waits for the Clerk org id, which is
    // what we want: skeletons until there is a real org to fetch for. A query
    // that has failed is no longer pending, so a catalogue error releases the
    // skeleton on its own while a records fetch still in flight keeps it.
    isLoading:
      !isError && !hasNoOrg && (catalogue.isPending || records.isPending),
    isError,
    isCatalogueUnavailable,
    hasNoOrg,
    refetch: () => {
      void catalogue.refetch()
      void records.refetch()
    },
  }
}
